/**
 * Reorder rules for spreadsheet field lists (columns, sort, query):
 * - Flat fields (no parentField) stay above table sub-fields
 * - Flat fields may only swap with other flat fields
 * - Table sub-fields may only swap within the same parent table
 */

export const getHasParentField = (item: { parentField?: unknown | null } | null | undefined): boolean => {
  if (!item || !("parentField" in item)) {
    return false;
  }
  const parentField = item.parentField;
  return parentField !== null && parentField !== undefined;
};

export const getParentKey = (item: { parentField?: any | null } | null | undefined): string | number | null => {
  const parentField = item?.parentField;
  if (!parentField) {
    return null;
  }
  return (
    parentField.id ??
    parentField.fieldid ??
    parentField.fieldId ??
    parentField.name ??
    parentField.fieldname ??
    parentField.fieldName ??
    null
  );
};

export const canSwapFieldPositions = <T extends { parentField?: unknown | null }>(
  items: T[],
  fromIndex: number,
  toIndex: number
): boolean => {
  if (fromIndex === toIndex) {
    return false;
  }

  const movedItem = items[fromIndex];
  const replacedItem = items[toIndex];

  if (!movedItem || !replacedItem) {
    return false;
  }

  const movedHasParent = getHasParentField(movedItem);
  const replacedHasParent = getHasParentField(replacedItem);

  if (movedHasParent !== replacedHasParent) {
    return false;
  }

  if (movedHasParent && replacedHasParent && getParentKey(movedItem) !== getParentKey(replacedItem)) {
    return false;
  }

  return true;
};

export const canMoveFieldByDirection = <T extends { parentField?: unknown | null }>(
  items: T[],
  currentIndex: number,
  direction: "up" | "down"
): boolean => {
  const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

  if (newIndex < 0 || newIndex >= items.length) {
    return false;
  }

  if (!canSwapFieldPositions(items, currentIndex, newIndex)) {
    return false;
  }

  const movedHasParent = getHasParentField(items[currentIndex]);

  if (movedHasParent && direction === "up") {
    for (let i = newIndex; i < currentIndex; i++) {
      if (!getHasParentField(items[i])) {
        return false;
      }
    }
  }

  if (!movedHasParent && direction === "down") {
    for (let i = currentIndex + 1; i <= newIndex; i++) {
      if (getHasParentField(items[i])) {
        return false;
      }
    }
  }

  return true;
};

export const reorderFieldsByDrag = <T extends { parentField?: unknown | null }>(
  items: T[],
  dragIndex: number,
  hoverIndex: number
): T[] | null => {
  if (!canSwapFieldPositions(items, dragIndex, hoverIndex)) {
    return null;
  }

  const next = [...items];
  const draggedField = next.splice(dragIndex, 1)[0]!;
  next.splice(hoverIndex, 0, draggedField);
  return next;
};
